import React, { useState, useEffect } from 'react';
import { Card, Badge, Spin } from 'antd';
import { InfoCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import toast from 'react-hot-toast';
import { GetPaymentGatewayApi } from '../../../../../api/request/wallet/cashdeposit';
import { GetMyTransactionChargesApi } from '../../../../../api/request/wallet/deposit';
import { chargePercentForGateway, effectiveChargePercent } from '../../../../../utils/clientTransactionCharge';
import { handleApiError } from '../../../../../utils/handleApiError';



const PaymentMethodSelection = ({ onMethodSelect, selectedMethod, onWithdrawClick, paymentMethod }) => {
    const [paymentGateways, setPaymentGateways] = useState([]);
    const [paymentGatewaysMethods, setPaymentGatewaysMethods] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Processing time color mapping
    const getProcessingTimeColor = (processingTime) => {
        if (processingTime?.toLowerCase().includes('instant') || processingTime?.toLowerCase().includes('immediate')) {
            return 'green';
        } else if (processingTime?.toLowerCase().includes('hour') || processingTime?.toLowerCase().includes('1-2')) {
            return 'blue';
        } else {
            return 'yellow';
        }
    };

    // API call to fetch payment gateways for withdrawal
    const getPaymentGateway = async () => {
        setIsLoading(true);
        try {
            const { data } = await GetPaymentGatewayApi(2);
            let charges = [];
            try {
                const chargeResponse = await GetMyTransactionChargesApi();
                if (chargeResponse?.data?.success) charges = chargeResponse.data.data?.charges ?? [];
            } catch (error) {
            }
            if (data?.success) {
                const formattedGateways = data?.data?.map(gateway => {
                    return {
                        id: gateway.id,
                        title: gateway.name,
                        description: gateway.description || `Secure ${gateway.name} withdrawal`,
                        processingTime: gateway.processingTime || '24 Hours',
                        processingTimeColor: getProcessingTimeColor(gateway.processingTime),
                        fee: `${effectiveChargePercent(chargePercentForGateway(charges, gateway.id), gateway.fees)}%`,
                        gatewayFee: gateway.fees,
                        minAmount: gateway.minAmount ? `$${gateway.minAmount}` : '$10',
                        maxAmount: gateway.maxAmount ? `$${gateway.maxAmount}` : '$50,000',
                        icon: <img src={gateway.paymentLogoUrl} alt={gateway.name} className="w-8 h-8" />,
                        iconBg: 'bg-blue-100',
                        iconColor: 'blue',
                        buttonText: `Withdraw via ${gateway.name}`,
                        buttonColor: 'bg-gradient',
                        paymentLogoUrl: gateway.paymentLogoUrl,
                        value: gateway.value || gateway.id
                    };
                });
                setPaymentGateways(formattedGateways);
                const methods = [];

                for (let i = 0; i < formattedGateways.length; i++) {
                    const gateway = formattedGateways[i];

                    switch (gateway.title) {
                        case "Cregis":
                            methods.push(
                                {
                                    label: 'USDT_TRC20', methodValue: 0, ...gateway, icon: <img src={"https://crm.upforex.live/LogoImage/Crypto.png"} alt={gateway.name} className="w-8 h-8" />
                                },
                                { label: 'USDT_BEP20', methodValue: 1, ...gateway, icon: <img src={"https://crm.upforex.live/LogoImage/BEP_Logo.jpg"} alt={gateway.name} className="w-8 h-8" /> },
                                { label: 'USDT_ERC20', methodValue: 2, ...gateway, icon: <img src={"https://crm.upforex.live/LogoImage/ERC_20.jpg"} alt={gateway.name} className="w-8 h-8" /> },
                            );
                            break;

                        case "Unipayment":
                            methods.push(
                                { label: 'Card Payment', methodValue: 4, ...gateway, icon: <img src={"https://crm.upforex.com/LogoImage/stripe.png"} alt={gateway.name} className="w-8 h-8" /> }
                            );
                            break;

                        case "MyFatoorah":
                            methods.push(
                                { label: 'MyFatoorah', methodValue: 6, ...gateway, icon: <img src={"https://crm.upforex.live/LogoImage/MyFatoorah.jpeg"} alt={gateway.name} className="w-8 h-8" /> }
                            );
                            break;

                        case "Match2Pay":
                            methods.push(
                                { label: 'USDT_ERC20', methodValue: 5, ...gateway, paymentLogoUrl: "https://crm.upforex.com/LogoImage/ERC_20.jpg" }
                            );
                            break;

                        default:
                            methods.push({ label: gateway.title, methodValue: gateway.value || gateway.id, ...gateway, })
                            break;
                    }
                }

                // Optional: Remove duplicate methods
                const uniqueMethods = methods.filter(
                    (m, i, a) => a.findIndex(x => x.methodValue === m.methodValue) === i
                );

                setPaymentGatewaysMethods(uniqueMethods);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getPaymentGateway();
    }, []);
    
    useEffect(() => {
        if (!isLoading && paymentGateways.length > 0 && !selectedMethod) {
            onMethodSelect && onMethodSelect(paymentGateways[0]);
        }
    }, [isLoading, paymentGateways, selectedMethod, onMethodSelect]);

    const getProcessingTimeBadgeColor = (color) => {
        switch (color) {
            case 'green': return 'success';
            case 'yellow': return 'warning';
            case 'blue': return 'processing';
            default: return 'default';
        }
    };

    const handleMethodClick = (method) => {
        onMethodSelect && onMethodSelect(method);
    };

    return (
        <div className="w-full p-6 border-2 border-light rounded-md mt-3">
            {/* Header */}
            <div className="flex items-center justify-between mb-3">
                <div className='w-full flex justify-between'>
                    <h1 className="text-xl font-semibold text-gray-800">Select Withdrawal Method</h1>
                    <div className="flex items-center text-gray-600">
                        <InfoCircleOutlined className="mr-2" />
                        <span>All withdrawals are processed securely</span>
                    </div>
                </div>
            </div>

            {/* Loading State */}
            {isLoading ? (
                <div className="flex justify-center items-center py-20">
                    <Spin
                        indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
                        size="large"
                    />
                    <span className="ml-3 text-gray-600">Loading withdrawal methods...</span>
                </div>
            ) : paymentGateways.length === 0 ? (
                <div className="flex justify-center items-center py-20">
                    <div className="text-center">
                        <InfoCircleOutlined className="text-4xl text-gray-400 mb-4" />
                        <p className="text-gray-600">No withdrawal methods available at the moment.</p>
                        <button
                            onClick={getPaymentGateway}
                            className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    {/* Payment Methods Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {paymentGatewaysMethods.map((method) => (
                            <Card
                                key={method.methodValue}
                                className={`relative cursor-pointer border-2 ${selectedMethod?.methodValue === method.methodValue ? 'border-primary' : 'border-light'
                                    }`}
                                onClick={() => handleMethodClick(method)}
                            >
                                {/* Processing Time Badge */}
                                <div className="absolute top-4 right-4">
                                    <Badge
                                        status={getProcessingTimeBadgeColor(method.processingTimeColor)}
                                        text={method.processingTime}
                                        className="text-xs"
                                    />
                                </div>

                                <div className="p-6">
                                    {/* Icon and Title */}
                                    <div className="flex items-center mb-6">
                                        <div className={`${method.iconBg} ${method.iconColor} w-10 h-10 rounded-xl flex items-center justify-center mr-4`}>
                                            {method.icon}
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-gray-900">{method.label}</h3>
                                            <p className="text-gray-600 text-xs">{method.description}</p>
                                        </div>
                                    </div>

                                    {/* Payment Details */}
                                    <div className="space-y-4 mb-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Processing Time:</span>
                                            <span className="font-medium text-gray-900">{method.processingTime}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Fee:</span>
                                            <span className="font-medium text-gray-900">{method.fee}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-gray-600 text-sm">Min/Max:</span>
                                            <span className="font-medium text-gray-900">{method.minAmount} - {method.maxAmount}</span>
                                        </div>
                                    </div>

                                    {/* Action Button */}
                                    <button
                                        className={`${method.buttonColor} border-none text-white font-medium h-10 rounded-lg w-full`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMethodClick(method);
                                            onWithdrawClick && onWithdrawClick();
                                        }}
                                    >
                                        Withdraw via {method.label}
                                    </button>
                                </div>

                                {/* Selected Indicator */}
                                {selectedMethod?.methodValue === method.methodValue && (
                                    <div className="absolute top-0 left-0 w-full h-1 bg-primary"></div>
                                )}
                            </Card>
                        ))}
                    </div>

                    {/* Footer Info */}
                    <div className="mt-3 p-4">
                        <div className="flex items-center justify-center text-gray-600 text-sm">
                            <InfoCircleOutlined className="mr-2" />
                            <span>All withdrawal methods are secured with industry-standard encryption. Processing times may vary based on your bank or payment provider.</span>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default PaymentMethodSelection;